import { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { differenceInHours, format, isThisYear } from "date-fns";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { FaCrown } from "react-icons/fa";
import {
  FiArrowUpRight,
  FiCheck,
  FiExternalLink,
  FiLink,
} from "react-icons/fi";

import { Avatar } from "MVVM/Components/AvatarV2";
import { DropdownMenuItem } from "MVVM/Components/DropdownMenu";
import { ClipEligibilityModal } from "MVVM/Components/Modals/ClipEligibilityModal";
import { usePortal } from "MVVM/Hooks/usePortal";
import { useUser } from "MVVM/Hooks/useUser";
import { dateFormatWithDistanceSuffix } from "MVVM/Utilities/Clip";
import { getWeaponIconUrl } from "MVVM/Views/TimelineEditor/utils";
import { getMapIconUrl } from "MVVM/Views/MatchHistory/utils";
import { RankBadge } from "MVVM/Views/Match/RankBadge";
import { buildShareUrl } from "MVVM/Views/Match/ShareMatchButton";
import { useCopyLink } from "MVVM/Views/Match/useCopyLink";
import { useRequestClip } from "MVVM/Views/Match/useRequestClip";
import {
  kdRatio,
  momentRound,
  momentState,
  momentTitle,
  momentWeapon,
  playerIdentity,
  toneFor,
} from "MVVM/Views/Match/utils";
import {
  AllstarMark,
  AvatarCrown,
  AvatarCrownWrap,
  TONE_COLOR,
  TStatTone,
} from "MVVM/Views/Match/styledComponents";
import { trackFeedClick } from "../../analytics";
import { IActivityFeedPlayedMatchItem, TFeedMatchStats } from "../@types";
import {
  Card,
  MatchActions,
  MatchArt,
  MatchArtScrim,
  MatchHeader,
  MatchHeading,
  MatchLeaderboard,
  MatchLeaderboardRating,
  MatchLeaderboardRow,
  MatchLobbyToggle,
  MatchMapTile,
  MatchMenu,
  MatchMenuButton,
  MatchMomentAction,
  MatchMomentBody,
  MatchMomentError,
  MatchMomentMeta,
  MatchMomentPanel,
  MatchMomentStatus,
  MatchMomentTitle,
  MatchOutcome,
  MatchPlay,
  MatchPrimaryAction,
  MatchRank,
  MatchSecondaryAction,
  MatchResult,
  MatchScore,
  MatchStat,
  MatchStats,
  MatchStatsRow,
  MatchTime,
  MatchTitle,
  MatchWeapon,
} from "./styledComponents";

const RESULT_LABEL: Record<string, string> = {
  Win: "Victory",
  Loss: "Defeat",
  Tie: "Draw",
};

const formatStat = (value: number | null | undefined, digits = 1) =>
  value === null || value === undefined ? "-" : value.toFixed(digits);

export const processedLabel = (processedAt: string, now = new Date()) => {
  const date = new Date(processedAt);
  if (differenceInHours(now, date) < 24)
    return dateFormatWithDistanceSuffix(processedAt);
  return format(date, isThisYear(date) ? "MMM d" : "MMM d, yyyy");
};

const statsFor = (
  viewer: TFeedMatchStats,
  withAdr: boolean,
): { label: string; value: string; tone: TStatTone }[] => {
  const kd = kdRatio(viewer);
  return [
    {
      label: "Rating",
      value: formatStat(viewer.rating, 2),
      tone: toneFor(viewer.rating, 1.3, 1.15, 0.95, 0.85),
    },
    {
      label: "K/D",
      value: kd.toFixed(2),
      tone: toneFor(kd, 1.6, 1.15, 0.85, 0.7),
    },
    ...(withAdr
      ? [
          {
            label: "ADR",
            value: formatStat(viewer.adr),
            tone: toneFor(viewer.adr, 100, 85, 65, 50),
          },
        ]
      : []),
    {
      label: "Headshot",
      value: `${formatStat(viewer.headshotPercentage)}%`,
      tone: toneFor(viewer.headshotPercentage, 65, 50, 35, 25),
    },
  ];
};

const TEAM_SHOWN = 2;

const PlayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" aria-hidden>
    <path d="M4 2.5v11l9-5.5z" fill="currentColor" />
  </svg>
);

export const PlayedMatchFeedCard = ({
  item,
}: {
  item: IActivityFeedPlayedMatchItem;
}) => {
  const { allstarUser } = useUser();
  const [showAllPlayers, setShowAllPlayers] = useState(false);
  const history = useHistory();
  const { copied, copy } = useCopyLink();
  const {
    portalOpen: eligibilityOpen,
    setPortalOpen: setEligibilityOpen,
    Portal: EligibilityPortal,
  } = usePortal(
    <ClipEligibilityModal
      surface="Activity Feed"
      clipQuota={allstarUser?.user?.clipQuota}
      userGroup={allstarUser?.user?.userGroup?.type}
      isCs2Clip
      onClose={() => setEligibilityOpen(false)}
    />,
  );
  const { requested, failed, requestClip } = useRequestClip({
    onIneligible: () => setEligibilityOpen(true),
    surface: "activity_feed",
    matchId: item.match?.id,
  });

  const { match, playerGameId } = item;
  const viewer = match?.playerStats.find(
    (row) => row.playerGameId === playerGameId,
  );
  if (!match || !viewer) return null;

  const playersById = Object.fromEntries(
    match.players.map((player) => [player.gameId, player]),
  );
  const mapIcon = getMapIconUrl(match.hero ?? undefined);

  const lobby = match.playerStats
    .filter((row) => row.rating != null)
    .sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
  const topRatedId = lobby[0]?.playerGameId;

  const [ownScore, opponentScore] = (match.score ?? "").split(":");
  const mapName = match.title?.split(" on ").slice(1).join(" on ") || "";
  const result = RESULT_LABEL[match.result ?? ""];
  const hasRank = (viewer.rankNew ?? viewer.rank) != null;

  const topMoment = match.moments[0];
  const clipState = topMoment ? momentState(topMoment, requested) : null;
  const weapon = topMoment ? momentWeapon(topMoment) : undefined;
  const round = topMoment ? momentRound(topMoment) : undefined;
  const clipError = topMoment ? failed[topMoment.id] : undefined;

  const matchUrl = (tab?: string) =>
    `/match/${match.id}?playerGameId=${playerGameId}${
      tab ? `&tab=${tab}` : ""
    }`;
  const track = (target: string) => () =>
    trackFeedClick("match_card", { target, matchId: match.id });

  const menuItems: DropdownMenuItem[] = [
    {
      id: "copy-link",
      label: "Copy match link",
      icon: <FiLink />,
      onClick: () => {
        track("copy_link")();
        copy(buildShareUrl(match.id, playerGameId, "overview"));
      },
    },
    {
      id: "match-page",
      label: "Visit match page",
      icon: <FiArrowUpRight />,
      onClick: () => {
        track("overview")();
        history.push(matchUrl());
      },
    },
  ];

  const lobbyGroups = viewer.team
    ? [
        {
          label: "Your team",
          rows: lobby.filter((row) => row.team === viewer.team),
        },
        {
          label: "Enemy team",
          rows: lobby.filter((row) => row.team !== viewer.team),
        },
      ]
    : [{ label: "Players", rows: lobby }];
  const visibleRows = (rows: TFeedMatchStats[]) =>
    showAllPlayers
      ? rows
      : rows.filter((row, index) => index < TEAM_SHOWN || row === viewer);
  const shownCount = lobbyGroups.reduce(
    (count, group) => count + visibleRows(group.rows).length,
    0,
  );
  const showLobbyToggle = showAllPlayers || shownCount < lobby.length;

  const outcome = (
    <MatchOutcome>
      {ownScore && opponentScore && (
        <MatchScore
          $result={match.result}
          aria-label={`${ownScore} to ${opponentScore}`}
        >
          <b>{ownScore}</b>
          <span aria-hidden>{"–"}</span>
          {opponentScore}
        </MatchScore>
      )}
      {result && <MatchResult $result={match.result}>{result}</MatchResult>}
    </MatchOutcome>
  );

  const stats = (
    <MatchStats>
      {statsFor(viewer, !topMoment).map((stat) => (
        <MatchStat key={stat.label} $color={TONE_COLOR[stat.tone]}>
          <dt>{stat.label}</dt>
          <dd>{stat.value}</dd>
        </MatchStat>
      ))}
      {hasRank && (
        <MatchRank>
          <dt>Rank</dt>
          <dd>
            <RankBadge stats={viewer} />
          </dd>
        </MatchRank>
      )}
    </MatchStats>
  );

  const clipThumbnail =
    clipState === "ready" ? topMoment?.clip?.thumbnailUrl : undefined;

  const watchUrl =
    clipState === "ready" && topMoment?.clip?.shareId
      ? `/clip/${topMoment.clip.shareId}`
      : undefined;

  const renderDuel = (row: TFeedMatchStats) => {
    if (row.team === viewer.team) return null;
    const duel = viewer.duels?.find(
      (entry) => entry.opponent === row.playerGameId,
    );
    if (!duel) return "-";
    const tone =
      duel.won > duel.lost
        ? "lime"
        : duel.lost > duel.won
        ? "caution"
        : "chalk";
    return (
      <MatchLeaderboardRating
        $color={tone}
        aria-label={`You ${duel.won}, them ${duel.lost}`}
      >
        {duel.won}
        {"\u2013"}
        {duel.lost}
      </MatchLeaderboardRating>
    );
  };

  const renderLobbyRow = (row: TFeedMatchStats) => {
    const player = playersById[row.playerGameId];
    const person = playerIdentity(player, row.playerGameId);
    return (
      <MatchLeaderboardRow key={row.playerGameId} $viewer={row === viewer}>
        <th scope="row">
          <Link
            to={
              person.username
                ? `/u/${person.username}`
                : `/match-history?playerSearchIdentifier=${encodeURIComponent(
                    row.playerGameId,
                  )}`
            }
            title={
              person.username
                ? `${person.name} on Allstar`
                : `${person.name}'s match history`
            }
            onClick={track(person.username ? "profile" : "match_history")}
          >
            <AvatarCrownWrap>
              <Avatar
                size="base"
                src={person.avatar}
                userGroup={person.userGroup}
                rank={person.rank}
              />
              {row.playerGameId === topRatedId && (
                <AvatarCrown title="Top rating in this match">
                  <FaCrown />
                </AvatarCrown>
              )}
            </AvatarCrownWrap>
            <span data-text>
              <span data-line>
                <span data-name>{person.name}</span>
                {person.username && <AllstarMark aria-hidden />}
              </span>
              {(row.rankNew ?? row.rank) != null && (
                <RankBadge stats={row} showChange={false} />
              )}
            </span>
          </Link>
        </th>
        <td>{renderDuel(row)}</td>
        <td>{kdRatio(row).toFixed(2)}</td>
        <td>{formatStat(row.adr)}</td>
        <td>
          <MatchLeaderboardRating
            $color={TONE_COLOR[toneFor(row.rating, 1.3, 1.15, 0.95, 0.85)]}
          >
            {formatStat(row.rating, 2)}
          </MatchLeaderboardRating>
        </td>
      </MatchLeaderboardRow>
    );
  };

  const renderMomentAction = () => {
    if (!topMoment) return null;
    if (watchUrl) {
      return (
        <MatchPlay aria-hidden>
          <PlayIcon />
        </MatchPlay>
      );
    }
    if (clipState === "rendering") {
      return <MatchMomentStatus>Rendering clip&hellip;</MatchMomentStatus>;
    }
    const createClip = () => {
      track("create_top_moment")();
      requestClip(topMoment.id);
    };
    return (
      <MatchMomentAction as="button" type="button" onClick={createClip}>
        {clipState === "failed" || clipError ? "Try again" : "Create clip"}
      </MatchMomentAction>
    );
  };

  const momentContent = topMoment && (
    <>
      {(clipThumbnail || match.hero) && (
        <MatchArt
          src={clipThumbnail || match.hero || ""}
          alt=""
          $clipFrame={!!clipThumbnail}
        />
      )}
      <MatchArtScrim />
      <MatchMomentBody>
        {weapon && <MatchWeapon src={getWeaponIconUrl(weapon)} alt="" />}
        <MatchMomentTitle>{momentTitle(topMoment)}</MatchMomentTitle>
        <MatchMomentMeta>
          {round ? `Round ${round} · ` : ""}Your top moment
        </MatchMomentMeta>
        {clipError && (
          <MatchMomentError role="alert">{clipError}</MatchMomentError>
        )}
      </MatchMomentBody>
      {renderMomentAction()}
    </>
  );

  return (
    <Card>
      {eligibilityOpen && EligibilityPortal}
      <MatchHeader>
        <MatchMapTile $art={match.hero} aria-hidden>
          {mapIcon && <img src={mapIcon} alt="" />}
        </MatchMapTile>
        <MatchHeading>
          <MatchTitle>
            Your {match.gameMode && <strong>{match.gameMode}</strong>} match
            {mapName && (
              <>
                {" "}
                on <strong>{mapName}</strong>
              </>
            )}
          </MatchTitle>
          <MatchTime dateTime={item.processedAt}>
            {processedLabel(item.processedAt)}
          </MatchTime>
        </MatchHeading>
        {outcome}
        <MatchMenu
          items={menuItems}
          trigger={
            <MatchMenuButton
              type="button"
              $copied={copied}
              aria-label={copied ? "Link copied" : "More options"}
              aria-live="polite"
            >
              {copied ? (
                <FiCheck size={18} aria-hidden />
              ) : (
                <BiDotsHorizontalRounded size={20} aria-hidden />
              )}
            </MatchMenuButton>
          }
        />
      </MatchHeader>

      {topMoment ? (
        watchUrl ? (
          <MatchMomentPanel
            as={Link}
            to={watchUrl}
            aria-label={`Watch ${momentTitle(topMoment)}`}
            onClick={track("watch_top_moment")}
          >
            {momentContent}
          </MatchMomentPanel>
        ) : (
          <MatchMomentPanel>{momentContent}</MatchMomentPanel>
        )
      ) : null}

      <MatchStatsRow>
        {stats}
        <MatchActions>
          {topMoment ? (
            <>
              <MatchSecondaryAction
                to={matchUrl()}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View match (opens in a new tab)"
                onClick={track("overview")}
              >
                View match
                <FiExternalLink size={14} aria-hidden />
              </MatchSecondaryAction>
              <MatchPrimaryAction
                to={matchUrl("match-clips")}
                onClick={track("clips")}
              >
                {match.momentCount === 1
                  ? "View your clip"
                  : `View your ${match.momentCount} clips`}
              </MatchPrimaryAction>
            </>
          ) : (
            <MatchPrimaryAction
              to={matchUrl()}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View match (opens in a new tab)"
              onClick={track("overview")}
            >
              View match
              <FiExternalLink size={14} aria-hidden />
            </MatchPrimaryAction>
          )}
        </MatchActions>
      </MatchStatsRow>

      {lobby.length > 2 && (
        <MatchLeaderboard aria-label="Lobby">
          <table>
            <thead>
              <tr>
                <th scope="col">Player</th>
                <th scope="col" title="Your kills and deaths against them">
                  Vs you
                </th>
                <th scope="col">K/D</th>
                <th scope="col">ADR</th>
                <th scope="col">Rating</th>
              </tr>
            </thead>
            {lobbyGroups
              .filter((group) => group.rows.length > 0)
              .map((group) => (
                <tbody key={group.label}>
                  <tr>
                    <th scope="colgroup" colSpan={5}>
                      {group.label}
                    </th>
                  </tr>
                  {visibleRows(group.rows).map(renderLobbyRow)}
                </tbody>
              ))}
          </table>
          {showLobbyToggle && (
            <MatchLobbyToggle
              type="button"
              aria-expanded={showAllPlayers}
              onClick={() => setShowAllPlayers((open) => !open)}
            >
              {showAllPlayers
                ? "Show less"
                : `Show all ${lobby.length} players`}
            </MatchLobbyToggle>
          )}
        </MatchLeaderboard>
      )}
    </Card>
  );
};
